import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SpotifyIntegration = ({ identifiedTrack, onClose }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState('beatseek');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [accessToken, setAccessToken] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check for existing Spotify token
    const token = localStorage.getItem('spotify_access_token');
    if (token) {
      setAccessToken(token);
      validateToken(token);
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const response = await axios.get(`${backendUrl}/api/spotify/user/info`, {
        params: { access_token: token }
      });
      setUserProfile(response.data);
      setIsConnected(true);
      loadUserPlaylists(token);
    } catch (error) {
      console.error('Token validation failed:', error);
      localStorage.removeItem('spotify_access_token');
      setIsConnected(false);
    }
  };

  const handleOAuthCallback = async (code) => {
    try {
      setLoading(true);
      const response = await axios.post(`${backendUrl}/api/spotify/auth/token`, null, {
        params: { code }
      });
      
      const token = response.data.access_token;
      localStorage.setItem('spotify_access_token', token);
      localStorage.setItem('spotify_refresh_token', response.data.refresh_token);
      
      setAccessToken(token);
      await validateToken(token);
      
      // Clean up URL safely
      if (window.history && window.location) {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } catch (error) {
      console.error('OAuth callback failed:', error);
      setMessage('Failed to connect to Spotify. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPlaylists = async (token) => {
    try {
      const response = await axios.get(`${backendUrl}/api/spotify/playlists`, {
        params: { access_token: token }
      });
      setPlaylists(response.data.playlists);
    } catch (error) {
      console.error('Failed to load playlists:', error);
    }
  };

  const handleSpotifyLogin = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/spotify/auth/url`);
      
      // Open Spotify OAuth in the same window (simpler approach)
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Login failed:', error);
      setMessage('Failed to initiate Spotify login. Please try again.');
      setLoading(false);
    }
  };

  const handleSaveTrack = async () => {
    if (!identifiedTrack || !accessToken) return;

    try {
      setLoading(true);
      const response = await axios.post(`${backendUrl}/api/spotify/save-track`, {
        track_name: identifiedTrack.title,
        artist_name: identifiedTrack.artist,
        playlist_choice: selectedPlaylist,
        access_token: accessToken
      });

      if (response.data.success) {
        setMessage(response.data.message);
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to save track:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save track to Spotify';
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    setIsConnected(false);
    setUserProfile(null);
    setPlaylists([]);
    setAccessToken(null);
  };

  if (!isConnected) {
    return (
      <div className="spotify-integration">
        <div className="spotify-modal-overlay" onClick={onClose}>
          <div className="spotify-modal" onClick={(e) => e.stopPropagation()}>
            <div className="spotify-modal-header">
              <h3>Connect to Spotify</h3>
              <button className="close-button" onClick={onClose}>×</button>
            </div>
            
            <div className="spotify-modal-content">
              <div className="spotify-connect-info">
                <div className="spotify-icon">🎵</div>
                <h4>Save "{identifiedTrack?.title}" to Spotify</h4>
                <p>Connect your Spotify account to save this track to your playlists</p>
              </div>
              
              <button 
                className="spotify-connect-button"
                onClick={handleSpotifyLogin}
                disabled={loading}
              >
                {loading ? 'Connecting...' : 'Connect Spotify Account'}
              </button>
              
              {message && <div className="spotify-message error">{message}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="spotify-integration">
      <div className="spotify-modal-overlay" onClick={onClose}>
        <div className="spotify-modal" onClick={(e) => e.stopPropagation()}>
          <div className="spotify-modal-header">
            <h3>Save to Spotify</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          
          <div className="spotify-modal-content">
            <div className="spotify-user-info">
              <div className="user-details">
                <span>Connected as: <strong>{userProfile?.display_name}</strong></span>
                <button className="disconnect-button" onClick={handleDisconnect}>
                  Disconnect
                </button>
              </div>
            </div>
            
            <div className="track-info">
              <h4>Saving: "{identifiedTrack?.title}"</h4>
              <p>by {identifiedTrack?.artist}</p>
            </div>
            
            <div className="playlist-selection">
              <h4>Choose destination:</h4>
              
              <div className="playlist-option">
                <label>
                  <input
                    type="radio"
                    name="playlist"
                    value="beatseek"
                    checked={selectedPlaylist === 'beatseek'}
                    onChange={(e) => setSelectedPlaylist(e.target.value)}
                  />
                  <span className="playlist-option-content">
                    <strong>BEATSEEK Discoveries</strong>
                    <small>Auto-create or use existing BEATSEEK playlist</small>
                  </span>
                </label>
              </div>
              
              {playlists.length > 0 && (
                <div className="existing-playlists">
                  <h5>Or choose an existing playlist:</h5>
                  {playlists.slice(0, 5).map((playlist) => (
                    <div key={playlist.id} className="playlist-option">
                      <label>
                        <input
                          type="radio"
                          name="playlist"
                          value={playlist.id}
                          checked={selectedPlaylist === playlist.id}
                          onChange={(e) => setSelectedPlaylist(e.target.value)}
                        />
                        <span className="playlist-option-content">
                          <strong>{playlist.name}</strong>
                          <small>{playlist.tracks_total} tracks</small>
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="spotify-actions">
              <button 
                className="save-button"
                onClick={handleSaveTrack}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Track'}
              </button>
            </div>
            
            {message && (
              <div className={`spotify-message ${message.includes('added') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotifyIntegration;